import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/api";

export function ImportPreview({ p, busy, onCancel, onConfirm, prog }: { p: any; busy: boolean; onCancel: () => void; onConfirm: () => void; prog?: string }) {
  const [view, setView] = useState<"nc" | "c">("nc");
  if (!p) return null;
  const cur = p.not_cleared ? (p.added ? view : "nc") : "c";
  const rows = cur === "nc" ? p.not_cleared_rows : p.clearances;
  const nothing = !p.added && !p.not_cleared;
  return (
    <Dialog open={!!p} onOpenChange={(o) => !o && !busy && onCancel()}>
      <DialogContent className="max-w-3xl rounded-2xl" data-testid="dow-import-preview">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Check this import before saving</DialogTitle>
          <DialogDescription className="text-[15px]" data-testid="dow-import-preview-counts">
            {p.not_cleared} not cleared · {p.added} clearance(s){p.expired ? ` (${p.expired} already expired)` : ""}
            {p.duplicates ? ` · ${p.duplicates} already in the portal (skipped)` : ""}
            {p.skipped_rows.length ? ` · ${p.skipped_rows.length} row(s) skipped (missing a name or date, or Cleared is not Yes/No: rows ${p.skipped_rows.slice(0, 8).join(", ")}${p.skipped_rows.length > 8 ? "…" : ""})` : ""}
          </DialogDescription>
        </DialogHeader>
        {p.future > 0 && (
          <details className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-[#92400E]" data-testid="dow-import-future">
            <summary className="cursor-pointer font-semibold">{p.future} row(s) not imported: the date is in the future (often a 2-digit year like "4/3/28"). Fix them in the file and import again.</summary>
            <ul className="mt-2 max-h-40 overflow-auto text-xs">{p.future_rows.map((f: any) => <li key={f.row}>Row {f.row}: {f.name}, date "{f.date}" read as {f.read_as}</li>)}</ul>
          </details>
        )}
        {nothing ? <p className="rounded-xl bg-[#F1F5F9] p-4 text-sm text-[#475569]" data-testid="dow-import-preview-empty">{p.future ? "Nothing new to add. The rows above have future dates and were skipped; everyone else is already in the portal." : "Nothing new to add. Everyone in this file is already in the portal."}</p> : (
          <>
            {p.added > 0 && p.not_cleared > 0 && (
              <div className="inline-flex gap-1 rounded-xl border border-slate-200 bg-white p-1">
                {([["nc", `Not cleared (${p.not_cleared})`], ["c", `Clearances (${p.added})`]] as const).map(([k, l]) => (
                  <button key={k} onClick={() => setView(k)} data-testid={`dow-import-preview-tab-${k}`}
                    className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${cur === k ? "bg-[#1B365D] text-white" : "text-[#475569] hover:bg-[#F1F5F9]"}`}>{l}</button>
                ))}
              </div>
            )}
            <p className="text-xs text-[#64748B]">{cur === "nc" ? "These go to Not approved, \"Not cleared in the old system\". No notices are sent." : "These go to the Clearances list."}</p>
            <div className="max-h-[50vh] overflow-auto rounded-xl border border-slate-200">
              <table className="tbl" data-testid="dow-import-preview-table">
                <thead><tr><th>Name</th><th>Parish / school</th><th>Ministry</th><th>{cur === "nc" ? "Date not cleared" : "Issued"}</th>{cur === "c" && <th>Expires</th>}</tr></thead>
                <tbody>{rows.map((r: any, i: number) => (
                  <tr key={i} data-testid="dow-import-preview-row">
                    <td className="font-semibold">{r.name}<p className="text-xs font-normal text-[#94A3B8]">{r.email}</p></td>
                    <td className="text-sm">{r.location || "\u2014"}{r.charter && <p className="text-xs text-[#94A3B8]">Charter {r.charter}</p>}</td>
                    <td className="text-xs">{r.position || "\u2014"}</td>
                    <td className="font-mono text-xs">{(cur === "nc" ? r.decided_at : r.issued_at) ? fmtDate(cur === "nc" ? r.decided_at : r.issued_at) : "\u2014"}</td>
                    {cur === "c" && <td className="font-mono text-xs">{r.expires_at ? fmtDate(r.expires_at) : "\u2014"}</td>}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          </>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button className="btn-secondary btn-sm justify-center" disabled={busy} onClick={onCancel} data-testid="dow-import-preview-cancel">Cancel</button>
          {!nothing && (
            <button className="btn-primary btn-sm justify-center" disabled={busy} onClick={onConfirm} data-testid="dow-import-preview-confirm">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {busy && prog ? <span data-testid="dow-import-preview-progress">{prog}</span> : <>Import {p.added + p.not_cleared} record(s)</>}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
